import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { IonLabel, IonSegment, IonSegmentButton } from '@ionic/react';
import { Ban, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { RideStatus } from '@tcrd/shared';
import { Card, cn, ErrorState, RideStatusBadge, Skeleton } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { api, errorMessage } from '../../../lib/api';
import { fmtTime } from '../../../lib/format';

type View = 'day' | 'week' | 'month';

interface CalendarData {
  rides: Array<{ id: string; code: string; status: RideStatus; leg: string; start: string; end: string; service: string; origin: string; destination: string; customer: string | null; rescheduleProposedAt: string | null }>;
  blackouts: Array<{ id: string; reason: string; note: string | null; start: string; end: string }>;
}

const TZ = 'America/Santo_Domingo';
const dayKey = (d: Date | string) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(d));
/** Medianoche local de Santo Domingo (UTC-4 fijo). */
const localMidnight = (key: string) => new Date(`${key}T00:00:00-04:00`);
const addDays = (key: string, n: number) => dayKey(new Date(localMidnight(key).getTime() + n * 86_400_000 + 3600_000));
const BLACKOUT_LABEL: Record<string, string> = { MAINTENANCE: 'Mantenimiento', VACATION: 'Vacaciones', UNAVAILABLE: 'No disponible', OTHER: 'Bloqueo' };

function range(view: View, anchor: string): { from: string; to: string; days: string[] } {
  if (view === 'day') return { from: anchor, to: addDays(anchor, 1), days: [anchor] };
  if (view === 'week') {
    const dow = (localMidnight(anchor).getUTCDay() + 6) % 7; // lunes = 0 (ajuste UTC-4 dentro del mismo día)
    const start = addDays(anchor, -dow);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return { from: start, to: addDays(start, 7), days };
  }
  const [y, m] = anchor.split('-').map(Number);
  const first = `${y}-${String(m).padStart(2, '0')}-01`;
  const dow = (new Date(`${first}T12:00:00-04:00`).getUTCDay() + 6) % 7;
  const gridStart = addDays(first, -dow);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return { from: gridStart, to: addDays(gridStart, 42), days };
}

export function CalendarPage() {
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState(dayKey(new Date()));
  const r = useMemo(() => range(view, anchor), [view, anchor]);
  const q = useQuery({
    queryKey: ['rides', 'calendar', r.from, r.to],
    queryFn: () => api<CalendarData>('/bookings/calendar', { query: { from: localMidnight(r.from).toISOString(), to: localMidnight(r.to).toISOString() } }),
  });

  const byDay = useMemo(() => {
    const m = new Map<string, CalendarData['rides']>();
    q.data?.rides.forEach((x) => m.set(dayKey(x.start), [...(m.get(dayKey(x.start)) ?? []), x]));
    return m;
  }, [q.data]);
  const blockedDays = useMemo(() => {
    const s = new Set<string>();
    q.data?.blackouts.forEach((b) => {
      for (let k = dayKey(b.start); k <= dayKey(new Date(new Date(b.end).getTime() - 1)); k = addDays(k, 1)) s.add(k);
    });
    return s;
  }, [q.data]);

  const step = (dir: number) => setAnchor((a) => (view === 'day' ? addDays(a, dir) : view === 'week' ? addDays(a, 7 * dir) : addDays(`${a.slice(0, 7)}-15`, 30 * dir)));
  const title = useMemo(() => {
    const opts: Intl.DateTimeFormatOptions = view === 'month' ? { month: 'long', year: 'numeric' } : { weekday: view === 'day' ? 'long' : undefined, day: 'numeric', month: 'long' };
    const a = new Date(`${view === 'week' ? r.from : anchor}T12:00:00-04:00`).toLocaleDateString('es-DO', { ...opts, timeZone: TZ });
    if (view !== 'week') return a;
    const b = new Date(`${addDays(r.to, -1)}T12:00:00-04:00`).toLocaleDateString('es-DO', { day: 'numeric', month: 'short', timeZone: TZ });
    return `${a} – ${b}`;
  }, [view, anchor, r]);
  const today = dayKey(new Date());

  const renderDayList = (key: string) => {
    const rides = byDay.get(key) ?? [];
    const blocks = q.data?.blackouts.filter((b) => dayKey(b.start) <= key && dayKey(new Date(new Date(b.end).getTime() - 1)) >= key) ?? [];
    return (
      <div key={key} className="flex flex-col gap-2">
        <h3 className={cn('text-sm font-semibold capitalize', key === today && 'text-brand-red')}>
          {new Date(`${key}T12:00:00-04:00`).toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'short', timeZone: TZ })}
        </h3>
        {blocks.map((b) => (
          <div key={b.id} className="flex items-center gap-2 rounded-xl bg-black/5 dark:bg-white/10 px-3 py-2 text-sm">
            <Ban className="size-4 text-[var(--text-muted)]" aria-hidden /> {BLACKOUT_LABEL[b.reason]} {b.note ? `· ${b.note}` : ''} ({fmtTime(b.start)}–{fmtTime(b.end)})
          </div>
        ))}
        {rides.length === 0 && blocks.length === 0 && <p className="text-sm text-[var(--text-muted)]">Libre</p>}
        {rides.map((x) => (
          <Link key={x.id} to={`/driver/rides/${x.id}`}>
            <Card className={cn('p-3 flex gap-3', ['CANCELLED', 'EXPIRED'].includes(x.status) && 'opacity-50')}>
              <div className="w-14 shrink-0 text-sm font-bold tabular-nums">{fmtTime(x.start)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 justify-between">
                  <p className="font-medium truncate">{x.customer ?? x.service}</p>
                  <RideStatusBadge status={x.status} />
                </div>
                <p className="text-xs text-[var(--text-muted)] truncate">{x.origin} → {x.destination}</p>
                {x.rescheduleProposedAt && <p className="text-xs text-amber-600 mt-1">Reprogramación pendiente</p>}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <Page
      title="Agenda"
      onRefresh={() => q.refetch()}
      actions={<Link to="/driver/blackouts" className="tap flex items-center justify-center px-2" aria-label="Bloquear horario"><Plus className="size-6" /></Link>}
    >
      <div className="flex flex-col gap-4">
        <IonSegment value={view} onIonChange={(e) => setView(e.detail.value as View)}>
          <IonSegmentButton value="day"><IonLabel>Día</IonLabel></IonSegmentButton>
          <IonSegmentButton value="week"><IonLabel>Semana</IonLabel></IonSegmentButton>
          <IonSegmentButton value="month"><IonLabel>Mes</IonLabel></IonSegmentButton>
        </IonSegment>
        <div className="flex items-center justify-between">
          <button className="tap flex items-center justify-center" onClick={() => step(-1)} aria-label="Anterior"><ChevronLeft className="size-6" /></button>
          <button className="text-base font-semibold capitalize" onClick={() => setAnchor(today)}>{title}</button>
          <button className="tap flex items-center justify-center" onClick={() => step(1)} aria-label="Siguiente"><ChevronRight className="size-6" /></button>
        </div>
        {q.isLoading && <Skeleton className="h-48" />}
        {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}

        {q.data && view === 'month' && (
          <>
            <div className="grid grid-cols-7 text-center text-xs text-[var(--text-muted)]">
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => <span key={d}>{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {r.days.map((k) => {
                const n = (byDay.get(k) ?? []).filter((x) => !['CANCELLED', 'EXPIRED'].includes(x.status)).length;
                const inMonth = k.slice(0, 7) === anchor.slice(0, 7);
                return (
                  <button
                    key={k}
                    onClick={() => (setAnchor(k), setView('day'))}
                    className={cn('tap aspect-square rounded-xl flex flex-col items-center justify-center text-sm', inMonth ? '' : 'opacity-35', k === today && 'ring-2 ring-brand-red', blockedDays.has(k) && 'bg-black/5 dark:bg-white/10')}
                    aria-label={`${k}: ${n} viajes`}
                  >
                    {Number(k.slice(8))}
                    {n > 0 && <span className="mt-0.5 min-w-4 rounded-full bg-brand-red text-white text-[10px] leading-4 px-1">{n}</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}
        {q.data && view !== 'month' && <div className="flex flex-col gap-5">{r.days.map(renderDayList)}</div>}
      </div>
    </Page>
  );
}
